import React, { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { Pagination } from '../common/Pagination';
import { ListEmpty, ListErrorCard, ListLoadingCard } from '../common/ListStateCard';
import { DeviceTransfer, TRANSFER_STATUSES, TransferBox, TransferStatus } from '../../types/deviceTransfer';
import { useTransfersList } from './portabilityList/useTransfersList';
import { TransferDetailModal } from './TransferDetailModal';
import { TransferStatusBadge } from './TransferStatusBadge';

/**
 * The take-over requests, one row each: the gateways, the two clients, the
 * template, the date, the status. A row opens the request.
 * The data lives in `useTransfersList`.
 */
export const TransfersTable: React.FC<{ refreshTrigger?: number }> = ({ refreshTrigger }) => {
  const { formatDate, formatNumber, t } = useI18n();
  const list = useTransfersList(refreshTrigger);
  const [selected, setSelected] = useState<DeviceTransfer | null>(null);

  if (list.loading && list.items.length === 0 && !list.error) return <ListLoadingCard message={t('portability.loading')} />;
  if (list.error) return <ListErrorCard title={t('portability.errorLoading')} message={list.error} onRetry={list.reload} />;

  const boxes: TransferBox[] = list.isAnepStaff ? ['all'] : ['inbox', 'sent'];

  return (
    <div className="tpl-container pt-container">
      <div className="tpl-card">
        <div className="tpl-toolbar">
          {boxes.length > 1 && (
            <div className="pt-boxes" role="tablist">
              {boxes.map((b) => (
                <button key={b} type="button" role="tab" aria-selected={list.box === b} className={`pt-box${list.box === b ? ' is-active' : ''}`} data-testid={`box-${b}`} onClick={() => list.setBox(b)}>
                  {t(`portability.boxes.${b}`)}
                </button>
              ))}
            </div>
          )}
          <select data-testid="filter-transfer-status" value={list.statusFilter} onChange={(e) => list.setStatusFilter(e.target.value as TransferStatus | '')} className="tpl-select" aria-label={t('portability.columns.status')}>
            <option value="">{t('portability.filterAllStatuses')}</option>
            {TRANSFER_STATUSES.map((s) => <option key={s} value={s}>{t(`portability.status.${s}`)}</option>)}
          </select>
          <div className="tpl-facts" data-testid="portability-facts">
            <span><strong>{formatNumber(list.total)}</strong> {t('portability.facts.total')}</span>
            {list.facts.toDecide > 0 && <span className="is-warning"><strong>{formatNumber(list.facts.toDecide)}</strong> {t('portability.facts.toDecide')}</span>}
          </div>
        </div>

        <div className="tpl-table-wrap">
          <table className="tpl-table pt-table" data-testid="transfers-table">
            <thead>
              <tr>
                <th>{t('portability.columns.devices')}</th>
                <th>{t('portability.columns.from')}</th>
                <th>{t('portability.columns.to')}</th>
                <th>{t('portability.columns.template')}</th>
                <th>{t('portability.columns.created')}</th>
                <th>{t('portability.columns.status')}</th>
                <th className="tpl-col-open"><span className="sr-only">{t('portability.open')}</span></th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((row) => (
                <tr key={row.id} className={`tpl-row pt-row${row.canDecide ? ' is-todo' : ''}`} data-testid={`transfer-row-${row.id}`} onClick={() => setSelected(row)}>
                  <td className="tpl-cell-name">
                    <strong className="tpl-name">{t('portability.deviceCount', { count: row.deviceCount, type: row.deviceType })}</strong>
                    <span className="tpl-sub mono">{row.deviceIds.slice(0, 2).join(', ')}{row.deviceIds.length > 2 ? ', …' : ''}</span>
                  </td>
                  <td>{row.fromClient.name ?? (row.fromClient.id ? <span className="tpl-muted mono">{row.fromClient.id}</span> : <span className="tpl-muted">{t('portability.hiddenOwner')}</span>)}</td>
                  <td>{row.toClient.name ?? <span className="tpl-muted mono">{row.toClient.id}</span>}</td>
                  <td>{row.templateName ?? <span className="tpl-muted">#{row.templateId}</span>}</td>
                  <td className="tpl-muted">{row.createdAt ? formatDate(row.createdAt) : '–'}</td>
                  <td>
                    <TransferStatusBadge status={row.status} />
                    {row.canDecide && <span className="pt-todo">{t('portability.toDecide')}</span>}
                  </td>
                  <td className="tpl-col-open"><span className="tpl-chevron" aria-hidden="true">›</span></td>
                </tr>
              ))}
            </tbody>
          </table>

          {list.items.length === 0 && !list.loading && (
            <ListEmpty title={t('portability.empty')} hint={list.statusFilter ? t('portability.emptyHint') : undefined} testId="portability-empty" />
          )}
        </div>

        <Pagination
          page={list.currentPage}
          totalPages={list.totalPages}
          totalItems={list.total}
          pageSize={list.itemsPerPage}
          itemLabel="requests"
          onPageChange={list.setCurrentPage}
          onPageSizeChange={(size) => { list.setItemsPerPage(size); list.setCurrentPage(1); }}
        />
      </div>

      <TransferDetailModal
        transfer={selected}
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); list.reload(); }}
      />
    </div>
  );
};
